import React from 'react';

import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/utils/cn';
import { pluralize } from '@/utils/string-utils';
import { formatPercent } from 'utils';
import { useAggregatedPerformanceReport, useHouseholdPortfolioPerformances } from 'views/portfolio/reporting-queries';

import { EM_DASH, formatFullCurrency } from '../../reporting/format';
import {
    gainCell,
    HEAD_CLASS,
    HEAD_ROW_CLASS,
    MV_CELL,
    RIGHT_HEAD_CLASS,
    TABLE_CARD_HEADER_CLASS,
    TABLE_CLASS,
    TABLE_FOOTER_CLASS,
} from '../../reporting/table-styles';

interface PerformanceBreakdownTableProps {
    householdGUID: string;
    fromInclusive: Date;
    toInclusive: Date;
    includeClosed: boolean;
}

// Full value with two decimals (matching client-portal) — used for Market Value and the totals
// row. Nullish coerces to $0.00 (these figures always render a number, never an em dash).
const money = (value?: string | number | null) => formatFullCurrency(Number(value ?? 0));
const percent = (value?: string | null) => (value != null ? formatPercent(Number(value)) : EM_DASH);

export const PerformanceBreakdownTable = ({
    householdGUID,
    fromInclusive,
    toInclusive,
    includeClosed,
}: PerformanceBreakdownTableProps) => {
    const { rows, portfolioCount, isLoading } = useHouseholdPortfolioPerformances({
        householdGUID,
        fromInclusive,
        toInclusive,
        includeClosed,
    });

    // The total row is the household total the API computes across all accounts (no
    // groupByGuids scoping) — the authoritative figure for every portfolio the table lists,
    // rather than a client-side re-sum of the rows (a sum of TWRs would also be meaningless).
    const { data: total, isPending: isTotalPending } = useAggregatedPerformanceReport({
        householdGUID,
        fromInclusive,
        toInclusive,
        includeClosed,
    });

    if (isLoading || isTotalPending) {
        return <div className="bg-cocoa-100 h-64 w-full animate-pulse rounded-2xl" />;
    }

    const accountCount = rows.reduce((sum, row) => sum + row.accountCount, 0);

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <div className={TABLE_CARD_HEADER_CLASS}>
                <h3 className="font-heading text-cocoa-1000 text-xl">Performance by Portfolio</h3>
            </div>
            <Table className={TABLE_CLASS}>
                <TableHeader>
                    <TableRow className={HEAD_ROW_CLASS}>
                        <TableHead className={HEAD_CLASS}>Portfolio</TableHead>
                        <TableHead className={HEAD_CLASS}>Accounts</TableHead>
                        <TableHead className={RIGHT_HEAD_CLASS}>Market Value</TableHead>
                        <TableHead className={RIGHT_HEAD_CLASS}>Net Earnings</TableHead>
                        <TableHead className={RIGHT_HEAD_CLASS}>Net Return (TWR)</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.portfolioGUID}>
                            <TableCell className="text-cocoa-1000 text-sm font-semibold">{row.name}</TableCell>
                            <TableCell className="text-cocoa-500 text-sm">
                                {pluralize(row.accountCount, 'account')}
                            </TableCell>
                            <TableCell className={MV_CELL}>{money(row.report?.endingValue)}</TableCell>
                            <TableCell className={gainCell(row.report?.netEarnings)}>
                                {formatFullCurrency(row.report?.netEarnings)}
                            </TableCell>
                            <TableCell className={gainCell(row.report?.returnsPercentNet)}>
                                {percent(row.report?.returnsPercentNet)}
                            </TableCell>
                        </TableRow>
                    ))}
                    {/* Same tinted total row as the Income section's projected-income table. */}
                    <TableRow className="bg-cocoa-100/40 hover:bg-cocoa-100/40">
                        <TableCell className="text-cocoa-1000 text-sm font-semibold">Total</TableCell>
                        <TableCell className="text-cocoa-500 text-sm">{pluralize(accountCount, 'account')}</TableCell>
                        <TableCell className={cn(MV_CELL, 'font-semibold')}>{money(total?.endingValue)}</TableCell>
                        <TableCell className={cn(gainCell(total?.netEarnings), 'font-semibold')}>
                            {formatFullCurrency(total?.netEarnings)}
                        </TableCell>
                        <TableCell className={cn(gainCell(total?.returnsPercentNet), 'font-semibold')}>
                            {percent(total?.returnsPercentNet)}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <div className={TABLE_FOOTER_CLASS}>{pluralize(portfolioCount, 'portfolio')}</div>
        </Card>
    );
};
